import { useEffect, useState } from 'react';
import { Button, Input } from '../../components/ui';
import { msUntilNextExpiry, olderBatchesLabel, splitBatchNotices } from './batchNotices';
import { batchSummary } from './leadSourceBatches';
import type { LeadSourceBatch } from './leadSourceBatches';

/**
 * The way back from a bulk fill, and the notice that announces one.
 *
 * The strip used to show all five recent batches, permanently. Kyu, looking at
 * the live Leads screen: "they stay up far too long and make the Leads screen
 * feel cluttered."
 *
 * So a fresh batch shows as a notice for thirty seconds and then goes quiet —
 * but it goes quiet BEHIND a control, not away. This strip is the only route
 * to reverting a bulk fill in the whole app, and an auto-dismiss that took the
 * undo with it would turn "that batch was wrong" from one click into 212 admin
 * corrections with a typed reason each.
 */
export function BatchUndoStrip({
  batches,
  onUndo,
  pending,
}: {
  batches: LeadSourceBatch[];
  onUndo: (batchId: string, reason: string, onDone: (count: number) => void) => void;
  pending: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  const [undoing, setUndoing] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [showOlder, setShowOlder] = useState(false);

  const { visible, overflow, older } = splitBatchNotices(batches, now);
  const nextExpiry = msUntilNextExpiry(batches, now);

  useEffect(() => {
    if (nextExpiry === null) {
      return;
    }
    // One timer for the next expiry, not a one-second interval running all
    // day. +50ms so the tick lands after the boundary rather than exactly on
    // it, where rounding can leave a notice up for one more render.
    const timer = setTimeout(() => {
      setNow(Date.now());
    }, nextExpiry + 50);
    return () => {
      clearTimeout(timer);
    };
  }, [nextExpiry]);

  if (batches.length === 0) {
    return null;
  }

  // A batch being undone stays on screen wherever it came from. Otherwise
  // typing a reason into a notice that ages out mid-sentence loses the words
  // and the row underneath the cursor.
  const undoingBatch = batches.find((batch) => batch.id === undoing) ?? null;
  const rows = showOlder ? [...visible, ...overflow, ...older] : visible;
  const hidden = overflow.length + older.length;

  const undoForm = (batch: LeadSourceBatch) => (
    <div key={batch.id} className="lead-batch-row">
      <Input
        label="Why undo this batch?"
        placeholder="e.g. wrong rule — these were inbound"
        value={reason}
        onChange={(event) => {
          setReason(event.target.value);
        }}
      />
      <Button
        size="sm"
        disabled={pending || reason.trim() === ''}
        onClick={() => {
          onUndo(batch.id, reason, () => {
            setUndoing(null);
            setReason('');
          });
        }}
      >
        Undo the batch
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setUndoing(null);
          setReason('');
        }}
      >
        Cancel
      </Button>
    </div>
  );

  const summaryRow = (batch: LeadSourceBatch) => (
    <div key={batch.id} className="lead-batch-row lead-batch-row--compact">
      <span className="tracker-field-note">{batchSummary(batch)}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setUndoing(batch.id);
          setReason('');
        }}
      >
        Undo
      </Button>
    </div>
  );

  return (
    <div className="lead-batch-strip">
      {undoingBatch !== null && undoForm(undoingBatch)}
      {rows.filter((batch) => batch.id !== undoing).map((batch) => summaryRow(batch))}
      {hidden > 0 && !showOlder && (
        <button
          type="button"
          className="lead-batch-more"
          aria-expanded={false}
          onClick={() => {
            setShowOlder(true);
          }}
        >
          {olderBatchesLabel(hidden)}
        </button>
      )}
      {showOlder && (
        <button
          type="button"
          className="lead-batch-more"
          aria-expanded
          onClick={() => {
            setShowOlder(false);
          }}
        >
          Hide bulk fills
        </button>
      )}
    </div>
  );
}
